// Copyright © Veeam Software Group GmbH

import React from 'react';
import { useModel } from '@veeam/core';
import {
    Button,
    BUTTON_VIEW,
    FormLayout,
    GROUP_DIRECTION,
    INDENT,
    LabelGroup,
    RadioField,
    StackView,
    STACK_ALIGN,
    STACK_DIRECTION,
    STACK_GAP,
    TextField,
} from '@veeam/components';
import { observeOn } from 'rxjs/operators';
import { asyncScheduler } from 'rxjs';

import type { Resources, LocalizableString } from 'infrastructure/resources';
import type { Fieldset } from 'infrastructure/types';
import type { UsePanelActions, WizardStepConfig } from 'components/wizardOld';
import type { Form } from '@veeam/core';
import type { FC } from 'react';

import { RestoreWizardPanelsEnum } from 'components/Restore/Wizards/types';
import { never } from 'infrastructure/never';
import { PageSection } from 'components/wizardOld';

export enum RestoreMode {
    Original = 'Original',
    AnotherPlace = 'AnotherPlace',
}

export interface FormWithRestoreMode {
    restoreMode: RestoreMode;
    listName: string;
    restoreChangedItems: boolean;
    restoreMissingItems: boolean;
    restorePermission: boolean;
    sendNotification: boolean;
    restoreOnlyLatest: boolean;
    overrideVersions: boolean;
}

type StepResources = Resources['features']['Restore']['Wizards']['SharePointRestoreWizard']['RestoreModeStep'];

function mapLabel(type: RestoreMode, resources: StepResources): LocalizableString {
    switch (type) {
        case RestoreMode.Original:
            return resources.originalLabel;
        case RestoreMode.AnotherPlace:
            return resources.anotherPlaceLabel;
        default:
            return never(type);
    }
}

const RestoreModeForm: FC<{
    form: Form<FormWithRestoreMode>;
    resources: StepResources;
    panelActions?: UsePanelActions<RestoreWizardPanelsEnum>;
}> = ({ form, resources, panelActions }) => {
    const {
        value: { restoreMode },
    } = useModel(form.model);
    return (
        <PageSection title={resources.title} description={resources.description}>
            <FormLayout direction={GROUP_DIRECTION.column}>
                <RadioField
                    label={resources.originalLabel}
                    helpText={resources.originalDescription}
                    field={form.fields.restoreMode}
                    value={RestoreMode.Original}
                />

                <LabelGroup
                    label={(
                        <RadioField
                            label={resources.anotherPlaceLabel}
                            helpText={resources.anotherPlaceDescription}
                            field={form.fields.restoreMode}
                            value={RestoreMode.AnotherPlace}
                        />
                    )}
                >
                    <StackView
                        spaceLeft={INDENT.l}
                        direction={STACK_DIRECTION.column}
                        gap={STACK_GAP.s}
                        align={STACK_ALIGN.start}
                    >
                        <TextField
                            field={form.fields.listName}
                            disabled={restoreMode !== RestoreMode.AnotherPlace}
                        />

                        <Button
                            ui={BUTTON_VIEW.text}
                            onClick={() => {
                                panelActions?.showPanel(RestoreWizardPanelsEnum.RestoreOptions, {
                                    panelMinWidth: '400px',
                                });
                            }}
                        >
                            {resources.showAdvancedOptionsLabel}
                        </Button>
                    </StackView>
                </LabelGroup>
            </FormLayout>
        </PageSection>
    );
};

export const useRestoreModeStep = (
    form: Form<FormWithRestoreMode>,
    resources: StepResources,
    panelActions?: UsePanelActions<RestoreWizardPanelsEnum>
): WizardStepConfig => ({
    tabTitle: resources.title,
    tag: 'items',
    form: {
        asObserver: () => form.model.asObservable().pipe(observeOn(asyncScheduler)),
        isValid: () => form.getMetaValue().isValid,
        submit: () => {
            form.validateAndDisplayErrors();
        },
    },
    body: <RestoreModeForm form={form} resources={resources} panelActions={panelActions} />,
});

interface Field {
    label: string;
    value: string;
}

export function getAdvancedOptionsSummaryFields(value: FormWithRestoreMode, resources: StepResources): Field[] {
    const fields = [
        {
            label: resources.restoreChangedItemsSummary,
            value: value.restoreChangedItems ? resources.yes : resources.no,
        },
        {
            label: resources.restoreMissingItemsSummary,
            value: value.restoreMissingItems ? resources.yes : resources.no,
        },
        {
            label: resources.restorePermissions,
            value: value.restorePermission ? resources.yes : resources.no,
        },
    ];

    if (value.restorePermission) {
        fields.push({
            label: resources.sendNotificationsSummary,
            value: value.sendNotification ? resources.yes : resources.no,
        });
    }

    fields.push({
        label: resources.restoreOnlyLatest,
        value: value.restoreOnlyLatest ? resources.yes : resources.no,
    });

    if (value.restoreOnlyLatest) {
        fields.push({
            label: resources.restoreOnlyLatestTypeSummary,
            value: value.overrideVersions ? resources.override : resources.merge,
        });
    }

    return fields;
}

export const getRestoreModeSummary = (value: FormWithRestoreMode, resources: StepResources): Fieldset => ({
    title: resources.restoreSummary,
    fields: [
        { label: resources.title, value: mapLabel(value.restoreMode, resources) },
        {
            label: resources.listNameLabel,
            value: value.listName,
            hidden: value.restoreMode === RestoreMode.Original,
        },
        ...getAdvancedOptionsSummaryFields(value, resources),
    ],
});
